import ActionButton from "@/components/buttons/ActionButton";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import { rangePickerFormItemProps, selectStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, DatePicker, Flex, Form, Radio, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../common/constants";
import { AgentCommonFilterFields } from "../../../agent/components/commonfilters/AgentCommonFilterFields";
import { AgentExtendedFilterModalForm } from "../../../agent/components/commonfilters/AgentExtendedFilterModalForm";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { CommissionsBatchesTreeSelect } from "../../../enumerations/components/form/CommissionsBatchesTreeSelect";
import { InstitutionTreeSelect } from "../../../enumerations/components/form/InstitutionTreeSelect";
import { ProductTreeSelect } from "../../../enumerations/components/form/ProductTreeSelect";
import { CommissionsBatchType } from "../../batch/enums";
import { DEFAULT_POINTS_QUERY_PARAMS } from "../api";
import { PointsReportType } from "../enums";
import { PointsReportFilterPageRequest } from "../types";

type ExtendedFilterFormType = PointsReportFilterPageRequest & {
  periodDateRange: [string, string];
  completedEducationDateRange?: string[];
  completedAcademyDateRange?: string[];
};

type Params = {
  searchParams: PointsReportFilterPageRequest;
};

export const PointsReportFilterForm = ({ searchParams }: Params) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const reportTypes = isTopAgentPlus
    ? [PointsReportType.NETTO_POINTS, PointsReportType.COMMISSION_AMOUNTS]
    : Object.values(PointsReportType).filter((type) => type !== PointsReportType.COMMISSION_AMOUNTS);

  const [form] = Form.useForm<ExtendedFilterFormType>();
  const institutionIds = Form.useWatch("institutionIds", form);
  const reportType = Form.useWatch("reportType", form);

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      periodDateRange:
        searchParams.periodStartDate && searchParams.periodEndDate
          ? [searchParams.periodStartDate, searchParams.periodEndDate]
          : undefined,
      completedEducationDateRange:
        searchParams.completedEducationDateMin && searchParams.completedEducationDateMax
          ? [searchParams.completedEducationDateMin, searchParams.completedEducationDateMax]
          : undefined,
      completedAcademyDateRange:
        searchParams.completedAcademyDateMin && searchParams.completedAcademyDateMax
          ? [searchParams.completedAcademyDateMin, searchParams.completedAcademyDateMax]
          : undefined,
    });
  }, [searchParams, form]);

  const handleFilterSubmit = ({
    periodDateRange,
    completedEducationDateRange,
    completedAcademyDateRange,
    ...filter
  }: ExtendedFilterFormType) => {
    const periodStartDate = periodDateRange?.[0];
    const periodEndDate = periodDateRange?.[1];

    // Sorting is bound to the report type and to the index of the displayed period, so it cannot outlive their change.
    const keepOrderBy =
      filter.reportType === searchParams.reportType &&
      periodStartDate === searchParams.periodStartDate &&
      periodEndDate === searchParams.periodEndDate;

    const updatedSearchParams = {
      ...filter,
      institutionIds: filter.reportType === PointsReportType.COMMISSION_AMOUNTS ? [] : filter.institutionIds,
      productIds: filter.reportType === PointsReportType.COMMISSION_AMOUNTS ? [] : filter.productIds,
      orderBy: keepOrderBy ? searchParams.orderBy : [],
      orderDirections: keepOrderBy ? searchParams.orderDirections : [],
      orderByPeriodIndex: keepOrderBy ? searchParams.orderByPeriodIndex : undefined,
      periodStartDate,
      periodEndDate,
      pageIndex: undefined,
    };

    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...updatedSearchParams,
            ...skipInitialFormValues(updatedSearchParams, DEFAULT_POINTS_QUERY_PARAMS),
          },
        }),
      },
      { replace: true },
    );
  };

  const handleFilterClear = () => {
    const { reportType: selectedReportType } = form.getFieldsValue();

    form.resetFields();
    form.setFieldsValue({ reportType: selectedReportType });

    handleFilterSubmit(form.getFieldsValue());
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("commissions.points.titles.report")}</h2>}
      extra={
        <ActionButton color="danger" size="middle" variant="outlined" icon="reload" onClick={handleFilterClear}>
          {t("common.resetFilter")}
        </ActionButton>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFilterSubmit} name="pointsReportForm">
        <Flex justify="center" className="margin-bottom-medium">
          <Form.Item name="reportType" noStyle initialValue={PointsReportType.NETTO_POINTS}>
            <Radio.Group
              size="large"
              buttonStyle="solid"
              optionType="button"
              onChange={(e) => {
                form.setFieldsValue({ reportType: e.target.value });
                handleFilterSubmit(form.getFieldsValue());
              }}
              options={reportTypes.map((type) => ({
                label: t(`commissions.points.enums.reportType.${type}`),
                value: type,
              }))}
            />
          </Form.Item>
        </Flex>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${isTopAgentPlus ? 6 : 5}, 1fr)`,
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <AgentCommonFilterFields>
            <Form.Item
              name="periodDateRange"
              label={t("commissions.points.filter.periodDateRange")}
              rules={[validations.none]}
              {...rangePickerFormItemProps("month")}
            >
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                format="MMMM YYYY"
                maxDate={dayjs()}
                placeholder={[t("common.from"), t("common.to")]}
                picker="month"
              />
            </Form.Item>
          </AgentCommonFilterFields>

          {reportType !== PointsReportType.COMMISSION_AMOUNTS ? (
            <>
              <Form.Item name="institutionIds" label={t("commissions.points.filter.institutionIds")}>
                <InstitutionTreeSelect />
              </Form.Item>

              <Form.Item
                name="productIds"
                label={
                  <LabelWithTooltip
                    tooltip={t("components.productTreeSelect.labelHint")}
                    label={t("components.productTreeSelect.label")}
                  />
                }
              >
                <ProductTreeSelect institutionIdsFilter={institutionIds} />
              </Form.Item>
            </>
          ) : undefined}

          {isTopAgentPlus ? (
            <Form.Item
              name="commissionBatchType"
              label={t("commissions.batch.enums.commissionBatchType_PLUS._label")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.values(CommissionsBatchType).map((type) => ({
                  value: type,
                  label: t(`commissions.batch.enums.commissionBatchType_PLUS.${type}`),
                }))}
              />
            </Form.Item>
          ) : undefined}

          <Form.Item name="commissionBatchIds" label={t("commissions.points.filter.commissionBatchIds")}>
            <CommissionsBatchesTreeSelect />
          </Form.Item>
        </div>

        <Flex justify="space-between">
          <AgentExtendedFilterModalForm
            onSubmit={(values) => {
              form.setFieldsValue(values);
              handleFilterSubmit(form.getFieldsValue());
            }}
          />

          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};
